import { Circle, Shield } from "@mui/icons-material";
import { ApiIncrementalityControlGroupType } from "@incendium/api";
import { Box, Divider, Stack, Typography, styled } from "@mui/material";
import {
  CONTROL_GROUP_LABEL,
  CONTROL_PERIOD_ALIGNMENT_LABEL,
  CONTROL_PERIOD_LABEL,
  GEO_SWITCH_OFF_LABEL,
  GEO_SWITCH_OFF_SUMMARY_DESCRIPTION,
  getControlGroupLabel,
  TEST_NAME_LABEL,
  TEST_PERIOD_LABEL,
  TEST_REGION_LABEL,
  TEST_SUMMARY_TITLE,
  TEST_TYPE_ACTION_LABEL,
  WHY_GEO_TESTS_BULLETS,
  WHY_GEO_TESTS_TITLE,
} from "../helpers/copy";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import { SectionTitle } from "./styled";
import moment from "moment";

const SummaryContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  background: theme.palette.background.paper,
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary,
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
}));

function IncrementalityTestSummary() {
  const { incrementalityTest, trendScore } = useIncrementalitySetupContext();
  const isAutoMatched =
    incrementalityTest.controlGroupType ===
    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED;

  return (
    <SummaryContainer>
      <Stack spacing={1.5}>
        <SectionTitle>{TEST_SUMMARY_TITLE}</SectionTitle>
        <Divider />

        <Box>
          <LabelText>{TEST_NAME_LABEL}</LabelText>
          <Typography fontWeight={600}>
            {incrementalityTest.name || "-"}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <LabelText>{TEST_TYPE_ACTION_LABEL}</LabelText>
          <Typography fontWeight={600}>{GEO_SWITCH_OFF_LABEL}</Typography>
          <Typography variant="body2" color="text.secondary">
            {GEO_SWITCH_OFF_SUMMARY_DESCRIPTION}
          </Typography>
        </Box>

        <Divider />
        <Box>
          <LabelText>{TEST_REGION_LABEL}</LabelText>
          <Stack direction="row" spacing={1} alignItems="center">
            <Circle sx={{ fontSize: 10, color: "primary.main" }} />
            <Typography fontWeight={600}>
              {[
                incrementalityTest.testRegionCity,
                incrementalityTest.testRegionCountry,
              ]
                .filter(Boolean)
                .join(", ") || "-"}
            </Typography>
          </Stack>
        </Box>

        <Divider />
        <Box>
          <LabelText>{CONTROL_GROUP_LABEL}</LabelText>
          <Stack direction="row" spacing={1} alignItems="center">
            <Circle sx={{ fontSize: 10, color: "success.main" }} />
            <Typography fontWeight={600}>
              {getControlGroupLabel(incrementalityTest.controlGroupType, {
                testRegionCity: incrementalityTest.testRegionCity,
                testRegionCountry: incrementalityTest.testRegionCountry,
              })}
            </Typography>
          </Stack>
        </Box>

        <Divider />
        <Box>
          <LabelText>{TEST_PERIOD_LABEL}</LabelText>
          <Typography fontWeight={600}>
            {incrementalityTest.testFrom && incrementalityTest.testTo
              ? `${moment(incrementalityTest.testFrom).format("MMM D, YYYY")} - ${moment(
                  incrementalityTest.testTo,
                ).format("MMM D, YYYY")}`
              : "-"}
          </Typography>
        </Box>

        <Divider />
        <Box>
          <LabelText>{CONTROL_PERIOD_LABEL}</LabelText>
          <Typography fontWeight={600}>
            {incrementalityTest.preTestFrom && incrementalityTest.preTestTo
              ? `${moment(incrementalityTest.preTestFrom).format("MMM D, YYYY")} - ${moment(
                  incrementalityTest.preTestTo,
                ).format("MMM D, YYYY")}`
              : "-"}
          </Typography>
        </Box>

        {isAutoMatched && (
          <>
            <Divider />
            <Box>
              <LabelText>{CONTROL_PERIOD_ALIGNMENT_LABEL}</LabelText>
              <Typography color="primary.main" variant="h1" fontWeight={700}>
                {trendScore}%
              </Typography>
            </Box>
          </>
        )}
      </Stack>
      <Box
        mt={4}
        sx={{
          border: (theme) => `1px solid ${theme.palette.success.light}`,
          background: (theme) => theme.palette.success.light + "24",
          borderRadius: 1.5,
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <Shield fontSize="small" color="success" />
          <Typography variant="subtitle2">{WHY_GEO_TESTS_TITLE}</Typography>
        </Stack>
        {WHY_GEO_TESTS_BULLETS.map((bullet) => (
          <Typography key={bullet} variant="caption" display="block">
            {bullet}
          </Typography>
        ))}
      </Box>
    </SummaryContainer>
  );
}

export default IncrementalityTestSummary;
